import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import JsBarcode from "jsbarcode";
import tunisianCities from "../../../../utils/cities"; // Assuming you have a list of cities in this module
import { fetchPlatform } from '../../platform/controller/platform-controller';

// Helper function to get delivery logo based on platform type
const getDeliveryLogo = (platformType) => {
  if (!platformType) return "/mylerz.png"; // Default to mylerz if no platform type
  
  const type = platformType.toLowerCase();
  
  if (type.includes('adex')) return "/adex.png";
  if (type.includes('insta')) return "/insta.png";
  if (type.includes('jax')) return "/jax.webp";
   if (type.includes('fiablo')) return "/fiablo.png";
      if (type.includes('navex')) return "/navex.jpeg";
  // Default to mylerz for any other platform type
  return "/mylerz.png";
};

// Update the PrintSettings to allow for variable height based on content
const PrintSettings = () => {
  return (
    <style type="text/css" media="print">
      {`
        @page {
          size: A4  landscape;
    margin-bottom: 0.75in;
            @bottom-left {
    content: counter(page) ' of ' counter(pages);
  }
        }
        
        body {
          margin: 0;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        @media print {
          html, body {
            width: 210mm;
            margin: 0;
            padding: 0;
          }
          
          .print-container {
            display: flex;
            flex-wrap: wrap;
            width: 210mm !important;
          }
          
          .order-sheet {
            width: 210mm !important;
            min-height: 148.5mm !important;
            page-break-inside: avoid;
            overflow: hidden;
            page-break-after: always;
          }

        }
      `}
    </style>
  );
};

// Updated component to accept an array of orders
const PdfExportMultiple = ({ orders = [], productItemsMap = {}, logo, mylerzLogo, isExchange = false }) => {
  console.log('teeeeeeeeeeeeeeest : ', isExchange);
  return (
    <>
      <PrintSettings />
      <div className="print-container">
        {orders.map((order, index) => (
          <OrderSheet 
            key={order.id || index}
            order={order}
            productItems={productItemsMap[order.id] || []}
            logo={logo}
            mylerzLogo={mylerzLogo}
            isExchange={isExchange} // Pass the isExchange prop
          />
        ))}
      </div>
    </>
  );
};

// Update the OrderSheet component to accept and use isExchange parameter
const OrderSheet = ({ order = {}, productItems = [], logo, mylerzLogo, isExchange = false }) => {
  const [barcodeUrl, setBarcodeUrl] = useState("");
  const [deliveryLogo, setDeliveryLogo] = useState(mylerzLogo || "/mylerz.png");
  const [loadingPlatform, setLoadingPlatform] = useState(false);
  
  // Fetch platform data to get the delivery logo based on platform type
  useEffect(() => {
    const fetchPlatformData = async () => {
      // Check if order has a platformId
      if (!order.plateformId) {
        // No platform, use default mylerz logo
        setDeliveryLogo(mylerzLogo || "/mylerz.png");
        return;
      }
      
      try {
        setLoadingPlatform(true);
        const platformData = await fetchPlatform(order.plateformId);
        
        // Get the appropriate logo based on platform type
        const logo = getDeliveryLogo(platformData.typePlateform);
        setDeliveryLogo(logo);
      } catch (error) {
        console.error("Error fetching platform data:", error);
        // Fallback to mylerz logo on error
        setDeliveryLogo(mylerzLogo || "/mylerz.png");
      } finally {
        setLoadingPlatform(false);
      }
    };
    
    fetchPlatformData();
  }, [order.plateformId, mylerzLogo]);
  
  // Extract client information from the order (unchanged)
  const clientName = order.clientFirstName + ' ' +  order.clientLastName || order.clientName || "Fatma Jmal";
  const clientPhone = order.clientNumber || order.clientNumber || "27581326";
  const orderCode = order.reference ? order.reference : "O-1235";
  const orderPrice = order.totalTTC?.toFixed(3) || "150.000";
  const orderReference = order.reference || "CMD-2025-6748";
  const orderBarCode = order.reference ? `${order.reference}-01-01`.replace('CMD-', '') : "63770940336001-01-01";
  const orderDate = order.dateBonCommande 
    ? new Date(order.dateBonCommande).toLocaleString()
    : "13/04/2025, 18:09:02";
  
  // Extract city information (unchanged)
  const getCityName = (cityCode) => {
    if (!cityCode) return 'N/A';
    
    const city = tunisianCities.find(city => city.Code === cityCode);
    return city ? city.EnName : cityCode;
  };

  const cityCode = order.ville || "TUN";
  const cityName = getCityName(cityCode);
  
  const deliveryCityText = `DELIVERY ${cityCode} - ${cityCode}`;
  
  const deliveryAddress = order.clientAdresse || order.adresseLivraison || `${cityName}, ${cityName}, ${cityName}, City - Floor Apartment`;
  const city = order.ville ? (deliveryAddress.includes(getCityName(order.ville)) ? '' : `${getCityName(order.ville)}, `) : '';
  const fullAddress = city + deliveryAddress;
  
  // Function to get all products from the order
  const getAllProductsInfo = () => {
    // Check if we have detailed product items
    if (productItems && productItems.length > 0) {
      return productItems.map(item => ({
        name: item.product?.name || item.productVariant?.reference || "Produit",
        reference: item.product?.reference || item.productVariant?.reference || "",
        attributes: item.productVariant?.name || "",
        quantity: item.quantite || 1,
        price: item.prixUnitaire?.toFixed(3) || "0.000"
      }));
    }
    
    // Fallback to the items from the order if productItems is not provided
    if (order.items && order.items.length > 0) {
      return order.items.map(item => ({
        name: item.reference || "F24",
        reference: item.reference || "",
        attributes: item.attributes || "noir 41",
        quantity: item.quantity || 1,
        price: item.price?.toFixed(3) || "N/A"
      }));
    }
    
    // Default values if no product info is available
    return [{
      name: "Produit",
      reference: "N/A",
      attributes: "N/A",
      quantity: 1,
      price: "N/A"
    }];
  };
  
  // Get all products in the order — flatten so each unit is a separate line
  const allProducts = getAllProductsInfo().flatMap(product =>
    Array.from({ length: product.quantity }, () => ({ ...product, quantity: 1 }))
  );
  
  // Extract shipment weight (unchanged)
  const weight = order.poids || "1.0";

  // Check if we have a delivery code (unchanged)
  const hasDeliveryCode = !!order.codeLivraison;
  const deliveryCode = order.codeLivraison || orderBarCode;
  
  // Generate barcode as data URL using JsBarcode (unchanged)
  useEffect(() => {
    if (hasDeliveryCode && deliveryCode) {
      try {
        // Create a canvas for the barcode
        const canvas = document.createElement('canvas');
        
        // Ensure we have valid text for the barcode
        const validBarcodeText = deliveryCode.trim() || "0000000000";
        
        // Generate the barcode with error handling
        JsBarcode(canvas, validBarcodeText, {
          format: "CODE128",
          displayValue: false,
          height: 40,
          width: 2,
          margin: 0,
          background: "#ffffff"
        });
        
        // Convert to data URL and update state
        const url = canvas.toDataURL('image/png');
        setBarcodeUrl(url);
      } catch (error) {
        console.error("Failed to generate barcode:", error);
        
        // Fallback to a simple placeholder barcode
        const fallbackCanvas = document.createElement('canvas');
        const ctx = fallbackCanvas.getContext('2d');
        fallbackCanvas.width = 200;
        fallbackCanvas.height = 40;
        
        // Draw alternating bars
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, fallbackCanvas.width, fallbackCanvas.height);
        ctx.fillStyle = '#000000';
        
        for (let i = 0; i < 20; i++) {
          if (i % 2 === 0) {
            ctx.fillRect(i * 10, 0, 5, fallbackCanvas.height);
          }
        }
        
        setBarcodeUrl(fallbackCanvas.toDataURL('image/png'));
      }
    }
  }, [hasDeliveryCode, deliveryCode]);

  // Calculate a dynamic height based on the number of products
  // Base height is 148mm for A5, add 15mm per additional product beyond the first
  const baseHeight = 148;
  const additionalHeight = Math.max(0, allProducts.length - 1) * 15;
  const sheetHeight = Math.max(baseHeight, baseHeight + additionalHeight);

  return (
    <div className="order-sheet">
      <div
        style={{
         width: "210mm",
        //  minHeight: `${sheetHeight}mm`,
          padding: "8mm",
          fontFamily: "Arial, sans-serif",
          fontSize: "12px",
          background: "#fff",
          boxSizing: "border-box",
          position: "relative",
          borderRadius: "2mm",
          display: "flex",
          flexDirection: "column",
          border: "1px solid #000",
        }}
      >
        {/* ÉCHANGE label - placed at the top of the document */}
        {isExchange && (
          <div style={{
            position: "absolute",
            top: "5mm",
            right: "10mm",
            color: "#e53e3e",
            fontWeight: "bold",
            fontSize: "24px",
            transform: "rotate(15deg)",
            border: "3px solid #e53e3e",
            padding: "6px 12px",
            borderRadius: "6px",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            boxShadow: "0 3px 6px rgba(0, 0, 0, 0.2)",
            zIndex: "5"
          }}>
            ÉCHANGE
          </div>
        )}

        {/* Top section with logo box and client/delivery information */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6mm" }}>
          {/* Left side - QR Code and client info */}
          <div style={{ width: "35%", display: "flex", flexDirection: "column" }}>
            {/* QR Code - only display if order.codeLivraison exists */}
            {hasDeliveryCode && (
              <div style={{ 
                width: "20mm", 
                height: "20mm", 
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "2mm",
                border: "1px solid #ddd",
                borderRadius: "1mm"
              }}>
                <QRCodeSVG 
                  value={deliveryCode}
                  size={65}
                  level="H"
                />
              </div>
            )}
            <div style={{ fontWeight: "bold", fontSize: "13px" }}>{clientName}</div>
            <div style={{ fontWeight: "bold", fontSize: "13px" }}>{clientPhone}</div>
            <div style={{ fontWeight: "bold", fontSize: "13px" }}>Code : {orderCode}</div>
          </div>

          {/* Center - Logo Box */}
          <div style={{ 
            width: "28%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "linear-gradient(to bottom, #ffffff, #f9f9f9)",
            border: "1px solid #d0d0d0",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05)",
            padding: "1mm",
            borderRadius: "3mm"
          }}>
            <div style={{ 
              width: "100%", 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center", 
              marginBottom: "0.5mm"
            }}>
              <img 
                src={deliveryLogo} 
                alt="Delivery Service" 
                style={{ 
                  height: "10mm", /* smaller delivery logo */
                  maxWidth: "80%", 
                  objectFit: "contain",
                  filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.08))"
                }} 
              />
            </div>
            
            <div style={{ 
              width: "90%",
              height: "1px",
              background: "#aaaaaa",
              margin: "0.5mm 0",
            }}></div>
            
            <div style={{ 
              width: "100%", 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center",
              marginTop: "0.5mm"
            }}>
              <img 
                src={logo} 
                alt="Logo" 
                style={{ 
                  height: "30mm", /* larger Tendresse/main logo for better prominence */
                  maxWidth: "95%", 
                  objectFit: "contain",
                  filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.12))"
                }} 
              />
            </div>
          </div>

          {/* Right side - Delivery info */}
          <div style={{ width: "35%", textAlign: "right", position: "relative" }}>
            <div style={{ fontWeight: "bold", fontSize: "13px" }}>DELIVERY KBL - KBL</div>
            <div style={{ fontWeight: "bold",fontSize: "13px" }}>CASH-ON-DELIVERY</div>
            <div style={{ 
              fontSize: "16px",
              fontWeight: "bold", 
              marginTop: "3mm",
              color: "#0f172a" 
            }}>
              {orderPrice} TND
            </div>
            {/* Authorization text */}
            <div style={{
              marginTop: "3mm",
              fontStyle: "italic",
              fontSize: "11px",
              color: "#444",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              padding: "1mm",
              borderRadius: "1mm",
              border: "1px solid #eee",
              fontWeight: "bold",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              display: "inline-block",
              maxWidth: "fit-content",
              marginTop: "10mm"
            }}>
              Autorisation d'ouverture de coli
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: "100%", borderTop: "1px solid #000", marginBottom: "4mm" }}></div> {/* Reduced from 5mm */}

        {/* Address section - CENTERED with larger font size (unchanged) */}
        <div style={{ marginBottom: "4mm", textAlign: "center" }}> {/* Reduced from 5mm */}
          <div style={{ 
            fontWeight: "bold",
            fontSize: "14px", // Reduced from 15px
            display: "inline-block",
            padding: "2mm 4mm", // Reduced horizontal padding from 5mm
            borderRadius: "2mm"
          }}>
            {fullAddress}
          </div>
        </div>

        {/* Another divider */}
        <div style={{ width: "100%", borderTop: "1px solid #000", marginBottom: "4mm" }}></div> {/* Reduced from 5mm */}

        {/* Reference and barcode section */}
        <div style={{
          borderRadius: "2mm",
          padding: "5mm",
          marginBottom: "5mm",
          flexGrow: 1,
          position: "relative"
        }}>
          
          {/* Centered barcode section - Only show if order.codeLivraison exists */}
          {hasDeliveryCode ? (
            <div style={{ 
              display: "flex", 
              flexDirection: "column", 
              alignItems: "center", 
              marginBottom: "3mm" // Reduced from 4mm
            }}>
              <div style={{ 
                color: "#000",
                padding: "1mm 6mm", // Reduced from 2mm 8mm
                fontWeight: "bold",
                fontSize: "12px", // Reduced from 13px
                marginBottom: "2mm", // Reduced from 3mm
                textAlign: "center"
              }}>
                {weight} Kg / {orderDate}
              </div>
              
              {/* Barcode */}
              {barcodeUrl ? (
                <img 
                  src={barcodeUrl} 
                  alt={deliveryCode}
                  style={{ 
                    width: "200px", // Reduced from 220px
                    height: "40px", // Reduced from 45px
                    display: "block" 
                  }}
                />
              ) : (
                <SimpleBarcode value={deliveryCode} width={200} height={40} /> // Reduced size
              )}
              
              <div style={{ textAlign: "center", fontWeight: "500", marginTop: "2mm", fontSize: "13px" }}> {/* Reduced from 3mm and 14px */}
                BLF : {deliveryCode}
              </div>
            </div>
          ) : (
            // Similar reductions for non-delivery code case
            <div style={{ 
              marginBottom: "3mm", // Reduced from 4mm
              textAlign: "center"
            }}>
              <div style={{ 
                color: "#000",
                padding: "1mm 6mm", // Reduced from 2mm 8mm
                fontWeight: "bold",
                fontSize: "12px", // Reduced from 13px
                marginBottom: "2mm", // Reduced from 3mm
              }}>
                {weight} Kg / {orderDate}
              </div>
              <div style={{ fontWeight: "bold", color: "#666", fontStyle: "italic", fontSize: "13px" }}> {/* Reduced from 14px */}
                Code de livraison non disponible
              </div>
            </div>
          )}
          
          {/* UPDATED: Product Description - Now displays all products with new format */}
          <div>
            <div style={{ 
              fontWeight: "bold", 
              marginBottom: "2mm", 
              fontSize: "13px", // Reduced from 14px
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <span>Description ({allProducts.length} {allProducts.length > 1 ? 'articles' : 'article'}):</span>
            </div>
            
            {/* Product list container with compact design */}
            <div style={{ 
              display: "flex", 
              flexDirection: "column", 
              gap: "2mm", // Increased from 1mm for better spacing
              maxHeight: allProducts.length > 4 ? "auto" : "auto",
              overflowY: allProducts.length > 4 ? "auto" : "visible",
              padding: allProducts.length > 4 ? "0 1mm 0 0" : "0",
              borderRadius: "1mm"
            }}>
              {allProducts.map((product, index) => (
                <div 
                  key={index}
                  className="products-list"
                  style={{
                    padding: "2mm", // Increased from 1mm for better padding
                    border: "1px solid #eee",
                    borderRadius: "1mm",
                    background: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
                    overflow:'hidden'
                  }}
                >
                  <div style={{ fontSize: "13px", lineHeight: "1.3" }}> {/* Increased from 11px and 1.1 */}
                    <span style={{ fontWeight: "bold" }}>Produit {index + 1}:</span> {product.name} 
                    {product.reference && ` (${product.reference})`}
                    {product.attributes && ` - ${product.attributes}`}
                    <span style={{ float: "right", fontWeight: "bold" }}>{product.price} TND</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple barcode component that renders visual bars (unchanged)
const SimpleBarcode = ({ value, width = 200, height = 40 }) => {
  // Generate a checksum from the value
  const checksum = [...value].reduce((acc, char) => acc + char.charCodeAt(0), 0) % 10;
  
  // Create a visual pattern based on the value
  const bars = [];
  for (let i = 0; i < value.length; i++) {
    const charCode = value.charCodeAt(i);
    const barWidth = (charCode % 3) + 1;
    const space = (charCode % 2) + 1;
    
    bars.push({
      width: barWidth,
      space: space
    });
  }
  
  // Add start, end and checksum
  bars.unshift({ width: 2, space: 1 }); // start
  bars.push({ width: 1, space: 0 }); // checksum
  bars.push({ width: 2, space: 0 }); // end
  
  // Calculate total width
  const totalWidth = bars.reduce((acc, bar) => acc + bar.width + bar.space, 0);
  const scale = width / totalWidth;
  
  return (
    <svg width={width} height={height}>
      <rect width={width} height={height} fill="white" />
      {bars.reduce((acc, bar, i) => {
        const x = acc;
        const barWidthScaled = bar.width * scale;
        const spaceWidthScaled = bar.space * scale;
        
        return acc + barWidthScaled + spaceWidthScaled;
      }, 0)}
      
      {bars.map((bar, i) => {
        const x = bars.slice(0, i).reduce((acc, b) => acc + (b.width + b.space) * scale, 0);
        return <rect key={i} x={x} y={0} width={bar.width * scale} height={height} fill="black" />;
      })}
    </svg>
  );
};

// Keep the original component as a single-order option
const PdfExportA5 = ({ order = {}, productItems = [], logo, mylerzLogo, isExchange = false }) => {
  return (
    <PdfExportMultiple 
      orders={[order]} 
      productItemsMap={{ [order.id || 'single']: productItems }} 
      logo={logo} 
      mylerzLogo={mylerzLogo}
      isExchange={isExchange} // Pass the isExchange prop
    />
  );
};

export { PdfExportMultiple, PdfExportA5 };
export default PdfExportA5;
